/**
 * Google Maps JS loader + Places Autocomplete hook.
 *
 * Loads the Maps JS API once (with places lib) and exposes a hook that
 * attaches a Places Autocomplete to any <input ref>.
 *
 * Usage:
 *   const ref = useRef(null);
 *   usePlacesAutocomplete(ref, (place) => setValue(place.formatted_address));
 *   <input ref={ref} />
 */
import { useEffect, useRef } from "react";

const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

let loadPromise = null;

export function loadGoogleMaps() {
  if (typeof window === "undefined") return Promise.reject(new Error("SSR"));
  if (window.google?.maps?.places) return Promise.resolve(window.google);
  if (loadPromise) return loadPromise;
  if (!API_KEY) return Promise.reject(new Error("Missing REACT_APP_GOOGLE_MAPS_API_KEY"));

  loadPromise = new Promise((resolve, reject) => {
    const cbName = `__gmapsInit_${Date.now()}`;
    window[cbName] = () => {
      try { delete window[cbName]; } catch (_) {}
      resolve(window.google);
    };
    const s = document.createElement("script");
    s.src = `https://maps.googleapis.com/maps/api/js?key=${API_KEY}&libraries=places&language=pt&region=PT&callback=${cbName}`;
    s.async = true;
    s.defer = true;
    s.onerror = () => reject(new Error("Failed to load Google Maps"));
    document.head.appendChild(s);
  });
  return loadPromise;
}

/**
 * Get driving distance + duration between two place_ids (or addresses).
 * @param {{place_id?: string, lat?: number, lng?: number, address?: string}} origin
 * @param {{place_id?: string, lat?: number, lng?: number, address?: string}} destination
 * @returns Promise<{distance_km: number, duration_min: number, polyline_encoded?: string, polyline_path?: {lat,lng}[]}>
 */
export async function getDirections(origin, destination) {
  const google = await loadGoogleMaps();
  const svc = new google.maps.DirectionsService();
  const toLoc = (p) => {
    if (p.place_id) return { placeId: p.place_id };
    if (p.lat && p.lng) return new google.maps.LatLng(p.lat, p.lng);
    return p.address;
  };
  const res = await svc.route({
    origin: toLoc(origin),
    destination: toLoc(destination),
    travelMode: google.maps.TravelMode.DRIVING,
    region: "PT",
  });
  const leg = res.routes[0]?.legs?.[0];
  if (!leg) throw new Error("no_route");
  return {
    distance_km: leg.distance.value / 1000,
    duration_min: leg.duration.value / 60,
    polyline_encoded: res.routes[0].overview_polyline,
    polyline_path: res.routes[0].overview_path?.map((p) => ({ lat: p.lat(), lng: p.lng() })),
  };
}

/**
 * Attaches a Places Autocomplete to a given input ref.
 * @param {React.RefObject<HTMLInputElement>} inputRef
 * @param {(place: {formatted_address: string, place_id?: string, lat?: number, lng?: number}) => void} onSelect
 * @param {object} options — { country: "pt" } etc.
 */
export function usePlacesAutocomplete(inputRef, onSelect, options = {}) {
  const acRef = useRef(null);

  useEffect(() => {
    let disposed = false;
    if (!inputRef.current) return;

    loadGoogleMaps().then((google) => {
      if (disposed || !inputRef.current) return;
      const ac = new google.maps.places.Autocomplete(inputRef.current, {
        fields: ["formatted_address", "place_id", "geometry", "name"],
        componentRestrictions: options.country ? { country: options.country } : { country: "pt" },
        types: options.types || ["geocode", "establishment"],
      });
      acRef.current = ac;
      ac.addListener("place_changed", () => {
        const p = ac.getPlace();
        onSelect?.({
          formatted_address: p.formatted_address || p.name || inputRef.current.value,
          place_id: p.place_id,
          lat: p.geometry?.location?.lat(),
          lng: p.geometry?.location?.lng(),
          name: p.name,
        });
      });
    }).catch((err) => {
      // Silent fail — form still works with plain input
      console.warn("[GoogleMaps] load failed:", err.message);
    });

    return () => {
      disposed = true;
      if (acRef.current) {
        try {
          // eslint-disable-next-line no-undef
          window.google?.maps?.event?.clearInstanceListeners(acRef.current);
        } catch (_) {}
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputRef]);
}
